/**
 * Turn an authorization code into a signed-in Cynosure session.
 *
 * Shared by both surfaces because the ordering matters and is not obvious:
 * native gets its code back from the browser sheet in-process, web gets it from
 * app/auth/callback.tsx after a full page navigation, but from the code onwards
 * the steps and their order are identical.
 */

import { Platform } from 'react-native';

import { authApi, userApi } from '../api/client';
import { useAuthStore } from '../stores/authStore';
import { useEncryptionStore } from '../stores/encryptionStore';
import { exchangeCode, takePendingLogin } from './oidc';
import { getRedirectUri } from './oidcConfig';
import { signIn } from './webSession';

export interface CompletedLogin {
  /** True when this login provisioned the Cynosure account. */
  created: boolean;
  /**
   * Where the caller that started this login asked to be sent back to, when it
   * asked at all. Only a step-up sets it; an ordinary login leaves it undefined
   * and the callback falls back to '/'.
   */
  returnTo?: string;
}

/**
 * Exchange, provision, then store — in that order, and the order is the point.
 *
 * The session must be established BEFORE the tokens are written to the store.
 * Once `setTokens` runs, `isAuthenticated` flips, the root layout's guard starts
 * routing on it, and every request the app fires carries the IdP access token.
 * If the local user row does not exist yet, all of those 401 with
 * `idp_session_required` — a login that appears to succeed and then immediately
 * fails everywhere. Provisioning first means the row exists before anything can
 * ask for it.
 */
export async function completeOidcLogin(code: string, state?: string): Promise<CompletedLogin> {
  if (Platform.OS === 'web') return completeWebLogin(code, state);

  const tokens = await exchangeCode(code, state);

  // The token just exchanged is passed EXPLICITLY, and the request interceptor
  // is written to let an explicit Authorization header win (api/client.ts).
  //
  // ⚠️  DO NOT "simplify" this to rely on the interceptor reading the store. The
  //     store is not empty here, which is the assumption this comment used to
  //     make and the reason logins broke: on web the callback is a full page
  //     load, so `authStore.initialize()` has already restored the previous
  //     session's access token — expired or not — before this runs. Sending
  //     that instead of the freshly exchanged one breaks the ID token's
  //     `at_hash` binding and the API answers 401. `setTokens` below is what
  //     replaces the stale value, and it has not run yet.
  const session = await authApi.establishIdpSession(tokens.idToken, tokens.accessToken);

  // If this is not the account the device last held, wipe that account's
  // session BEFORE anything below writes the new one. The guard existed with
  // no caller at all. Without it, a logout that did not finish — the tab closed
  // mid-redirect to the IdP — left user A's tokens, flags and encryption state
  // in place for user B to sign in on top of, and the root layout routed B past
  // the recovery prompt on A's `hasLocalKey`.
  await useAuthStore.getState().ensureCleanSessionForUser(session.data.user_id);

  // Set before setTokens, which flips `isAuthenticated` and wakes the root
  // layout's guard — so anything the guard might read has to be true by now.
  //
  // ⚠️  THIS NO LONGER DECIDES THE E2EE SCREEN, and must never be made to
  //     again. It used to: app/_layout.tsx read `isNewUser ? 'encryption' :
  //     'restore'`, seeded from the `created` of the single login that
  //     provisioned the row. That put a durable routing decision in a value
  //     only one window ever observes, and on 2026-08-04 a first login whose
  //     window died after provisioning left every later login routing to
  //     "restore" — asking for a recovery phrase that had never been
  //     generated, permanently. The layout now reads the server's
  //     `e2ee_setup_complete`, which is the same fact stored where a lost
  //     window cannot take it with them.
  //
  // What remains is an honest first-run signal: whether THIS login created the
  // account. Nothing routes on it today.
  useEncryptionStore.getState().setIsNewUser(session.data.created);

  // The id_token is kept, not discarded: it is the id_token_hint that ends the
  // session at the IdP on logout. Without it, logging out clears Cynosure and
  // leaves the provider's session alive, and the next sign-in needs no passkey.
  await useAuthStore
    .getState()
    .setTokens(tokens.accessToken, tokens.refreshToken, tokens.idToken);

  // Load the profile immediately rather than leaving it to the layout's retry
  // path: that one exists as a safety net for a failed login and force-logs-out
  // after ten seconds, which is not the experience a fresh login should get.
  const me = await userApi.getMe();
  await useAuthStore.getState().setUser(me.data);

  return { created: session.data.created, returnTo: tokens.returnTo };
}

/**
 * The web leg: the API exchanges the code, provisions the user in the same
 * round trip and sets the session cookies, so "provision before any request"
 * holds by construction. The same order as native from there on.
 */
async function completeWebLogin(code: string, state?: string): Promise<CompletedLogin> {
  const pending = await takePendingLogin(state);
  const session = await signIn(code, pending.verifier, getRedirectUri());

  // See the native path: wipe a different previous account before writing.
  await useAuthStore.getState().ensureCleanSessionForUser(session.userId);
  useEncryptionStore.getState().setIsNewUser(session.created);
  await useAuthStore.getState().setWebSession(session.accessToken, session.sessionId);

  const me = await userApi.getMe();
  await useAuthStore.getState().setUser(me.data);

  return { created: session.created, returnTo: pending.returnTo };
}
